import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { RootState, AppDispatch } from '../../store';
import { createEvent, clearError } from '../../store/eventsSlice';
import { JOIN_CODE_MIN, JOIN_CODE_MAX, JOIN_CODE_PATTERN } from '../../types/event';

interface CreateEventModalProps {
    show: boolean;
    onHide: () => void;
}

const CreateEventModal: React.FC<CreateEventModalProps> = ({ show, onHide }) => {
    const [name, setName] = useState('');
    const [startAt, setStartAt] = useState('');
    const [endsAt, setEndsAt] = useState('');
    const [isPublic, setIsPublic] = useState(false);
    const [joinCode, setJoinCode] = useState('');
    const [validated, setValidated] = useState(false);

    const dispatch = useDispatch<AppDispatch>();

    const { isLoading, error } = useSelector((state: RootState) => state.events);

    // Inline validation: only enforce the pattern when a value is provided
    // (join code is optional). Field is hidden unless isPublic is true.
    const isJoinCodeValid = joinCode === '' || JOIN_CODE_PATTERN.test(joinCode);

    const resetForm = () => {
        setName('');
        setStartAt('');
        setEndsAt('');
        setIsPublic(false);
        setJoinCode('');
        setValidated(false);
    };

    const handleClose = () => {
        resetForm();
        dispatch(clearError());
        onHide();
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;

        // Reject form when join code is set but malformed. The native
        // `pattern` attribute on the input fails closed via form.checkValidity().
        if (form.checkValidity() === false || (isPublic && !isJoinCodeValid)) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        setValidated(true);

        try {
            await dispatch(
                createEvent({
                    name: name.trim(),
                    start_at: startAt || null,
                    ends_at: endsAt || null,
                    is_public: isPublic,
                    join_code: isPublic && joinCode ? joinCode : null,
                })
            ).unwrap();

            handleClose();
        } catch (err) {
            // Error is handled by the Redux slice
            console.error('Event creation failed:', err);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Create New Event</Modal.Title>
            </Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert
                            variant="danger"
                            dismissible
                            onClose={() => dispatch(clearError())}
                        >
                            {error}
                        </Alert>
                    )}

                    <Form.Group className="mb-3" controlId="eventName">
                        <Form.Label>Event Name</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Enter event name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            minLength={1}
                            maxLength={127}
                            disabled={isLoading}
                            autoFocus
                        />
                        <Form.Control.Feedback type="invalid">
                            Please enter an event name (1-127 characters).
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="eventStartAt">
                        <Form.Label>Start Date (optional)</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={startAt}
                            onChange={(e) => setStartAt(e.target.value)}
                            disabled={isLoading}
                        />
                        <Form.Text className="text-muted">
                            When does the event start?
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="eventEndsAt">
                        <Form.Label>End Date (optional)</Form.Label>
                        <Form.Control
                            type="datetime-local"
                            value={endsAt}
                            onChange={(e) => setEndsAt(e.target.value)}
                            disabled={isLoading}
                            min={startAt || undefined}
                        />
                        <Form.Text className="text-muted">
                            When does the event end?
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="eventIsPublic">
                        <Form.Check
                            type="checkbox"
                            label="Public event"
                            checked={isPublic}
                            onChange={(e) => setIsPublic(e.target.checked)}
                            disabled={isLoading}
                        />
                        <Form.Text className="text-muted">
                            Visible in the public lobby — anyone can see this event
                            without logging in.
                        </Form.Text>
                    </Form.Group>

                    {/* Join code is hidden until the event is marked public. */}
                    {isPublic && (
                        <Form.Group className="mb-3" controlId="eventJoinCode">
                            <Form.Label>Join code (optional)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., ALPHA42"
                                value={joinCode}
                                onChange={(e) => setJoinCode(e.target.value)}
                                pattern={JOIN_CODE_PATTERN.source}
                                minLength={JOIN_CODE_MIN}
                                maxLength={JOIN_CODE_MAX}
                                disabled={isLoading}
                                isInvalid={validated && joinCode !== '' && !isJoinCodeValid}
                                aria-describedby="joinCodeHelp"
                            />
                            <Form.Control.Feedback type="invalid">
                                Join code must be {JOIN_CODE_MIN}–{JOIN_CODE_MAX} alphanumeric characters.
                            </Form.Control.Feedback>
                            <Form.Text id="joinCodeHelp" className="text-muted">
                                If set, players must enter this code to claim units in
                                this event&apos;s lobby.
                            </Form.Text>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={isLoading}>
                        {isLoading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Creating...
                            </>
                        ) : (
                            'Create Event'
                        )}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateEventModal;
